/**
 * Scene System V2 - Device Scene Capabilities Loader
 *
 * Preloads and manages scene capabilities from device configurations.
 * Provides centralized access to device actions, conditions, and triggers.
 */

import type {
	DeviceSceneCapabilities,
	CapabilityLoadResult,
	CapabilityError,
	CapabilityWarning,
	SceneExecutionContext,
} from '../types/scene-capabilities';
import { devices_config } from '@devices/index.js';

console.log('DeviceSceneCapabilities module loading...');
console.log('devices_config imported:', typeof devices_config, Object.keys(devices_config || {}).length);

export class DeviceSceneCapabilities {
	private static instance: DeviceSceneCapabilities;
	// Store capabilities per device type and mode: deviceType -> modeName -> capabilities
	private capabilities: Map<string, Map<string, DeviceSceneCapabilities>> = new Map();
	private loadedDeviceTypes: Set<string> = new Set();
	private loadErrors: CapabilityError[] = [];
	private loadWarnings: CapabilityWarning[] = [];

	private constructor() {}

	public static getInstance(): DeviceSceneCapabilities {
		if (!DeviceSceneCapabilities.instance) {
			DeviceSceneCapabilities.instance = new DeviceSceneCapabilities();
		}
		return DeviceSceneCapabilities.instance;
	}

	/**
	 * Initialize the capability system by preloading all device configurations
	 */
	public async initialize(): Promise<void> {
		console.log('DeviceSceneCapabilities: Starting initialization...');

		try {
			// Load all device configurations from /src/configs/devices/
			const deviceConfigs = await this.loadDeviceConfigurations();

			for (const [deviceType, config] of deviceConfigs) {
				const loadResults = await this.loadDeviceCapabilities(deviceType, config);

				// Store capabilities for each mode (even if validation has errors - we'll log them)
				if (loadResults.length > 0) {
					const modeCapabilities = new Map<string, DeviceSceneCapabilities>();
					let hasAnyMode = false;

					for (const result of loadResults) {
						if (result.modeName && result.capabilities) {
							// Store capabilities even if there are validation errors
							modeCapabilities.set(result.modeName, result.capabilities);
							hasAnyMode = true;

							// Log errors/warnings but don't block loading
							if (result.errors.length > 0) {
								console.warn(
									`DeviceSceneCapabilities: ${deviceType}.${result.modeName} has validation errors:`,
									result.errors,
								);
							}
						}
						this.loadErrors.push(...result.errors);
						this.loadWarnings.push(...result.warnings);
					}

					if (hasAnyMode) {
						this.capabilities.set(deviceType, modeCapabilities);
						this.loadedDeviceTypes.add(deviceType);
					}
				}
			}

			console.log(`DeviceSceneCapabilities: Loaded ${this.loadedDeviceTypes.size} device types`);

			if (this.loadErrors.length > 0) {
				console.warn(
					`DeviceSceneCapabilities: ${this.loadErrors.length} load errors encountered`,
				);
			}

			if (this.loadWarnings.length > 0) {
				console.warn(
					`DeviceSceneCapabilities: ${this.loadWarnings.length} warnings encountered`,
				);
			}
		} catch (error) {
			console.error('DeviceSceneCapabilities: Initialization failed:', error);
			throw new Error('Failed to initialize device scene capabilities');
		}
	}

	/**
	 * Get scene capabilities for a specific device and mode
	 * @param deviceType Device model/type
	 * @param mode Optional mode name. If not provided, returns capabilities for the first mode
	 */
	public getCapabilities(deviceType: string, mode?: string): DeviceSceneCapabilities | null {
		const modeCapabilities = this.capabilities.get(deviceType);

		if (!modeCapabilities) {
			console.warn(
				`DeviceSceneCapabilities: No capabilities found for device type: ${deviceType}`,
			);
			console.warn(
				`DeviceSceneCapabilities: Available device types:`,
				Array.from(this.capabilities.keys()).slice(0, 10),
			);
			return null;
		}

		// If mode is specified, get that specific mode
		if (mode) {
			const capabilities = modeCapabilities.get(mode);
			if (!capabilities) {
				console.warn(
					`DeviceSceneCapabilities: Mode '${mode}' not found for ${deviceType}. Available modes:`,
					Array.from(modeCapabilities.keys()),
				);
				return null;
			}
			return capabilities;
		}

		// Otherwise, return the first mode's capabilities
		const firstMode = Array.from(modeCapabilities.values())[0];
		return firstMode || null;
	}

	/**
	 * Get all available device types with scene capabilities
	 */
	public getLoadedDeviceTypes(): string[] {
		return Array.from(this.loadedDeviceTypes);
	}

	/**
	 * Check if a device type has scene capabilities loaded
	 */
	public hasCapabilities(deviceType: string): boolean {
		return this.capabilities.has(deviceType);
	}

	/**
	 * Get scene capabilities for a device with fallback handling
	 */
	public getCapabilitiesWithFallback(deviceType: string, mode?: string): DeviceSceneCapabilities {
		const capabilities = this.getCapabilities(deviceType, mode);

		if (capabilities) {
			return capabilities;
		}

		// Return minimal fallback capabilities
		return this.createFallbackCapabilities(deviceType);
	}

	/**
	 * Get available modes for a device type
	 */
	public getAvailableModes(deviceType: string): string[] {
		const modeCapabilities = this.capabilities.get(deviceType);
		if (!modeCapabilities) {
			return [];
		}
		return Array.from(modeCapabilities.keys());
	}

	/**
	 * Validate scene capabilities for a device configuration
	 */
	public validateCapabilities(
		deviceType: string,
		capabilities: DeviceSceneCapabilities,
	): CapabilityLoadResult {
		const errors: CapabilityError[] = [];
		const warnings: CapabilityWarning[] = [];

		console.log(`DeviceSceneCapabilities: Validating ${deviceType}`, {
			hasActions: !!capabilities?.actions,
			hasConditions: !!capabilities?.conditions,
			hasTriggers: !!capabilities?.triggers,
			capabilitiesType: typeof capabilities,
			capabilitiesKeys: capabilities ? Object.keys(capabilities) : [],
		});

		// Validate actions structure
		if (!capabilities.actions || typeof capabilities.actions !== 'object') {
			errors.push({
				type: 'invalid_structure',
				message: 'Device capabilities must include actions object',
				path: `${deviceType}.scene.actions`,
				severity: 'critical',
			});
		}

		// Validate conditions structure
		if (!capabilities.conditions || typeof capabilities.conditions !== 'object') {
			errors.push({
				type: 'invalid_structure',
				message: 'Device capabilities must include conditions object',
				path: `${deviceType}.scene.conditions`,
				severity: 'critical',
			});
		}

		// Validate triggers structure
		if (!capabilities.triggers || typeof capabilities.triggers !== 'object') {
			errors.push({
				type: 'invalid_structure',
				message: 'Device capabilities must include triggers object',
				path: `${deviceType}.scene.triggers`,
				severity: 'critical',
			});
		}

		// Validate individual actions
		for (const [actionName, action] of Object.entries(capabilities.actions || {})) {
			const actionErrors = this.validateSceneAction(deviceType, actionName, action);
			errors.push(...actionErrors);
		}

		// Validate individual conditions
		for (const [conditionName, condition] of Object.entries(capabilities.conditions || {})) {
			const conditionErrors = this.validateSceneCondition(deviceType, conditionName, condition);
			errors.push(...conditionErrors);
		}

		// Validate individual triggers
		for (const [triggerName, trigger] of Object.entries(capabilities.triggers || {})) {
			const triggerErrors = this.validateSceneTrigger(deviceType, triggerName, trigger);
			errors.push(...triggerErrors);
		}

		const result = {
			success: errors.length === 0,
			capabilities,
			errors,
			warnings,
		};

		console.log(`DeviceSceneCapabilities: Validation result for ${deviceType}:`, {
			success: result.success,
			errorCount: errors.length,
			firstError: errors[0],
		});

		return result;
	}

	/**
	 * Get load errors encountered during initialization
	 */
	public getLoadErrors(): CapabilityError[] {
		return [...this.loadErrors];
	}

	/**
	 * Get load warnings encountered during initialization
	 */
	public getLoadWarnings(): CapabilityWarning[] {
		return [...this.loadWarnings];
	}

	/**
	 * Create execution context for scene processing
	 */
	public createExecutionContext(
		deviceId: string,
		deviceType: string,
		additionalContext: Partial<SceneExecutionContext> = {},
	): SceneExecutionContext {
		const capabilities = this.getCapabilitiesWithFallback(deviceType);

		return {
			deviceId,
			deviceType,
			capabilities,
			currentState: {},
			executionId: this.generateExecutionId(),
			timestamp: Date.now(),
			...additionalContext,
		};
	}

	// Private methods

	private async loadDeviceConfigurations(): Promise<Map<string, any>> {
		const configs = new Map<string, any>();

		try {
			console.log('DeviceSceneCapabilities: Loading device configurations...');

			// Use the already-exported devices_config instead of glob patterns
			const deviceModels = Object.keys(devices_config);
			console.log(`DeviceSceneCapabilities: ${deviceModels.length} device models registered`);

			// Simply map the exported configs to our Map structure
			for (const modelName of deviceModels) {
				const config = devices_config[modelName];
				if (config) {
					configs.set(modelName, config);
				} else {
					console.warn(`DeviceSceneCapabilities: No config found for ${modelName}`);
				}
			}

			console.log(
				'DeviceSceneCapabilities: Loaded device configurations:',
				Array.from(configs.keys()),
			);
		} catch (error) {
			console.error('DeviceSceneCapabilities: Failed to load device configurations:', error);
		}

		return configs;
	}

	private async loadDeviceCapabilities(
		deviceType: string,
		config: any,
	): Promise<Array<CapabilityLoadResult & { modeName?: string }>> {
		const results: Array<CapabilityLoadResult & { modeName?: string }> = [];

		try {
			// Check if device has modes
			if (!config.modes || typeof config.modes !== 'object') {
				console.warn(`DeviceSceneCapabilities: ${deviceType} has no modes defined`);
				return [
					{
						success: false,
						capabilities: this.createFallbackCapabilities(deviceType),
						errors: [
							{
								type: 'missing_capability',
								message: `Device ${deviceType} has no modes defined`,
								path: `${deviceType}.modes`,
								severity: 'error',
							},
						],
						warnings: [],
					},
				];
			}

			// Load capabilities from each mode
			const modes = Object.entries(config.modes);

			if (modes.length === 0) {
				console.warn(`DeviceSceneCapabilities: ${deviceType} has empty modes object`);
				return [
					{
						success: false,
						capabilities: this.createFallbackCapabilities(deviceType),
						errors: [
							{
								type: 'missing_capability',
								message: `Device ${deviceType} has no modes`,
								path: `${deviceType}.modes`,
								severity: 'error',
							},
						],
						warnings: [],
					},
				];
			}

			for (const [modeName, modeConfig] of modes) {
				const sceneConfig = (modeConfig as any)?.sceneCapabilities;
				console.log(
					`DeviceSceneCapabilities: ${deviceType}.${modeName} - sceneConfig type:`,
					typeof sceneConfig,
				);
				console.log(
					`DeviceSceneCapabilities: ${deviceType}.${modeName} - has actions:`,
					!!sceneConfig?.actions,
				);
				if (!sceneConfig) {
					// Mode doesn't have scene capabilities - this is OK, not all modes need them
					continue;
				}

				// Validate the loaded capabilities
				const validationResult = this.validateCapabilities(deviceType, sceneConfig);
				results.push({
					...validationResult,
					modeName,
				});

				if (validationResult.success) {
					console.log(
						`DeviceSceneCapabilities: Loaded ${deviceType}.${modeName} capabilities`,
					);
				}
			}

			// If no modes had capabilities, try top-level as fallback for migration
			if (results.length === 0 && config.sceneCapabilities) {
				console.warn(
					`DeviceSceneCapabilities: ${deviceType} has top-level sceneCapabilities (deprecated). Should be moved to modes.${config.family || deviceType}`,
				);
				const validationResult = this.validateCapabilities(deviceType, config.sceneCapabilities);
				results.push({
					...validationResult,
					modeName: config.family || deviceType, // Use family as mode name
				});
			}

			if (results.length === 0) {
				results.push({
					success: false,
					capabilities: this.createFallbackCapabilities(deviceType),
					errors: [
						{
							type: 'missing_capability',
							message: `Device ${deviceType} has no automation capabilities in any mode`,
							path: `${deviceType}.modes.*.sceneCapabilities`,
							severity: 'error',
						},
					],
					warnings: [],
				});
			}
		} catch (error) {
			results.push({
				success: false,
				capabilities: this.createFallbackCapabilities(deviceType),
				errors: [
					{
						type: 'validation_failed',
						message: `Failed to load capabilities for ${deviceType}: ${error.message}`,
						path: deviceType,
						severity: 'critical',
					},
				],
				warnings: [],
			});
		}

		return results;
	}

	private validateSceneAction(deviceType: string, actionName: string, action: any): CapabilityError[] {
		const errors: CapabilityError[] = [];
		const path = `${deviceType}.scene.actions.${actionName}`;

		console.log(`Validating action ${deviceType}.${actionName}:`, {
			hasName: !!action.name,
			hasCommandTemplate: !!action.commandTemplate,
			hasExecuteAction: !!action.executeAction,
			actionKeys: Object.keys(action),
		});

		if (!action.name) {
			errors.push({
				type: 'invalid_structure',
				message: 'Action must have a name',
				path: `${path}.name`,
				severity: 'error',
			});
		}

		// Actions can have either commandTemplate (old structure) or executeAction (new structure)
		if (!action.commandTemplate && !action.executeAction) {
			errors.push({
				type: 'invalid_structure',
				message: 'Action must have either a command template or execute function',
				path: `${path}.commandTemplate`,
				severity: 'error',
			});
		}

		return errors;
	}

	private validateSceneCondition(
		deviceType: string,
		conditionName: string,
		condition: any,
	): CapabilityError[] {
		const errors: CapabilityError[] = [];
		const path = `${deviceType}.scene.conditions.${conditionName}`;

		if (!condition.name) {
			errors.push({
				type: 'invalid_structure',
				message: 'Condition must have a name',
				path: `${path}.name`,
				severity: 'error',
			});
		}

		// Conditions can have either evaluationType (old structure) or evaluateCondition (new structure)
		if (!condition.evaluationType && !condition.evaluateCondition) {
			errors.push({
				type: 'invalid_structure',
				message: 'Condition must have either an evaluation type or evaluate function',
				path: `${path}.evaluationType`,
				severity: 'error',
			});
		}

		return errors;
	}

	private validateSceneTrigger(
		deviceType: string,
		triggerName: string,
		trigger: any,
	): CapabilityError[] {
		const errors: CapabilityError[] = [];
		const path = `${deviceType}.scene.triggers.${triggerName}`;

		if (!trigger.name) {
			errors.push({
				type: 'invalid_structure',
				message: 'Trigger must have a name',
				path: `${path}.name`,
				severity: 'error',
			});
		}

		// Triggers can have either triggerType (old structure) or detectTrigger (new structure)
		if (!trigger.triggerType && !trigger.detectTrigger) {
			errors.push({
				type: 'invalid_structure',
				message: 'Trigger must have either a trigger type or detect function',
				path: `${path}.triggerType`,
				severity: 'error',
			});
		}

		return errors;
	}

	private createFallbackCapabilities(deviceType: string): DeviceSceneCapabilities {
		return {
			actions: {},
			conditions: {},
			triggers: {},
		};
	}

	private generateExecutionId(): string {
		return `exec_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
	}
}

// Export singleton instance
export const deviceSceneCapabilities = DeviceSceneCapabilities.getInstance();
